import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './Gallery.css';

const SWIPE_THRESHOLD_PX = 50;
const supportsPointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;

const GalleryLightbox = ({ isOpen, photo = null, onClose, onPrev, onNext }) => {
  const lightboxRef = useRef(null);
  const swipeStartRef = useRef(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        onNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, onNext, onPrev]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const node = lightboxRef.current;
    if (!node) {
      return undefined;
    }

    const getPoint = (event) => {
      if (event.touches && event.touches[0]) {
        return {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
      }
      if (event.changedTouches && event.changedTouches[0]) {
        return {
          x: event.changedTouches[0].clientX,
          y: event.changedTouches[0].clientY
        };
      }
      return {
        x: event.clientX ?? 0,
        y: event.clientY ?? 0
      };
    };

    const startSwipe = (event) => {
      swipeStartRef.current = getPoint(event);
      setIsDragging(true);
      setDragOffset(0);
    };

    const moveSwipe = (event) => {
      if (!swipeStartRef.current) {
        return;
      }

      const point = getPoint(event);
      const deltaX = point.x - swipeStartRef.current.x;
      const deltaY = point.y - swipeStartRef.current.y;

      if (Math.abs(deltaX) > Math.abs(deltaY) || Math.abs(deltaX) > 10) {
        setDragOffset(deltaX);
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    };

    const endSwipe = (event) => {
      if (!swipeStartRef.current) {
        return;
      }

      const endPoint = getPoint(event);
      const deltaX = endPoint.x - swipeStartRef.current.x;
      const deltaY = endPoint.y - swipeStartRef.current.y;

      setIsDragging(false);

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_THRESHOLD_PX) {
        if (deltaX > 0) {
          onPrev();
        } else {
          onNext();
        }
      }

      setDragOffset(0);
      swipeStartRef.current = null;
    };

    const cancelSwipe = () => {
      swipeStartRef.current = null;
      setIsDragging(false);
      setDragOffset(0);
    };

    if (supportsPointerEvents) {
      node.addEventListener('pointerdown', startSwipe);
      node.addEventListener('pointermove', moveSwipe);
      node.addEventListener('pointerup', endSwipe);
      node.addEventListener('pointercancel', cancelSwipe);
      node.addEventListener('pointerleave', cancelSwipe);

      return () => {
        node.removeEventListener('pointerdown', startSwipe);
        node.removeEventListener('pointermove', moveSwipe);
        node.removeEventListener('pointerup', endSwipe);
        node.removeEventListener('pointercancel', cancelSwipe);
        node.removeEventListener('pointerleave', cancelSwipe);
      };
    }

    const touchMoveOptions = { passive: false };

    node.addEventListener('touchstart', startSwipe, { passive: true });
    node.addEventListener('touchmove', moveSwipe, touchMoveOptions);
    node.addEventListener('touchend', endSwipe);
    node.addEventListener('touchcancel', cancelSwipe);
    node.addEventListener('mousedown', startSwipe);
    node.addEventListener('mousemove', moveSwipe);
    node.addEventListener('mouseup', endSwipe);
    node.addEventListener('mouseleave', cancelSwipe);

    return () => {
      node.removeEventListener('touchstart', startSwipe);
      node.removeEventListener('touchmove', moveSwipe);
      node.removeEventListener('touchend', endSwipe);
      node.removeEventListener('touchcancel', cancelSwipe);
      node.removeEventListener('mousedown', startSwipe);
      node.removeEventListener('mousemove', moveSwipe);
      node.removeEventListener('mouseup', endSwipe);
      node.removeEventListener('mouseleave', cancelSwipe);
    };
  }, [isOpen, onNext, onPrev]);

  if (!isOpen || !photo) {
    return null;
  }

  return (
    <div
      ref={lightboxRef}
      className="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Lightbox Ansicht"
    >
      <button className="close-btn" onClick={onClose} aria-label="Schließen">
        ×
      </button>
      <button className="prev-btn" onClick={onPrev} aria-label="Vorheriges Foto">
        ‹
      </button>
      <figure
        style={{
          transform: `translateX(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
          willChange: isDragging ? 'transform' : 'auto'
        }}
        data-testid="gallery-lightbox-figure"
      >
        <img src={photo.fullUrl} alt={photo.caption || 'Galerie Foto'} />
        {photo.caption && <figcaption>{photo.caption}</figcaption>}
      </figure>
      <button className="next-btn" onClick={onNext} aria-label="Nächstes Foto">
        ›
      </button>
    </div>
  );
};

GalleryLightbox.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  photo: PropTypes.shape({
    fullUrl: PropTypes.string,
    caption: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

export default GalleryLightbox;
